import { ITableColumnState } from 'hooks/useTableCurrentState';

import { ColoredDotCell } from 'components/common/Tables/Cells/ColoredDotCell/ColoredDotCell';
import { columnsCommonConfig } from 'components/common/Tables/columnsCommonConfig';
import { DefaultColumnFilter } from 'components/common/Tables/components/ColumnFilter/ColumnFilter';

import { ProjectCell } from 'components/widgets/ProjectScorecards/components/ProjectCell/ProjectCell';

import { getColorFromStatus } from 'utilitys/Status/Status.utils';

export const columns = [
    {
        Header: 'Project / Sub-program',
        ...columnsCommonConfig.projectNameSubProgram,
        width: 340,
        fixedSize: true,
        accessor: (rowOriginal) => `${rowOriginal.project.name} / ${rowOriginal.contract.name}`,

        Filter: ({ column }) => <DefaultColumnFilter placeholder={'Search by name'} column={column} />,
        Cell: ({ row }) => {
            const original = row.original;
            const title = `${original.project.name} / ${original.contract.name}`;
            return <ProjectCell title={title} versionHashCode={original.version_hash_code} />;
        },
    },
    {
        Header: 'Progress',
        ...columnsCommonConfig.progress,
        accessor: (rowOriginal) => rowOriginal.progress.value.toFixed(2),
        Cell: ({ row }) => {
            const progress = row.original.progress;
            return (
                <ColoredDotCell
                    color={getColorFromStatus(progress.fluxIndex, 'Progress')}
                    toolTipContent={String(progress.value)}
                />
            );
        },
    },
    {
        Header: 'Risk',
        ...columnsCommonConfig.risk,
        accessor: (rowOriginal) => rowOriginal.risk.value.toFixed(2),
        Cell: ({ row }) => {
            const risk = row.original.risk;
            return (
                <ColoredDotCell
                    color={getColorFromStatus(risk.fluxIndex, 'Risk')}
                    toolTipContent={String(risk.value)}
                />
            );
        },
    },
    {
        Header: 'Quality',
        ...columnsCommonConfig.quality,
        accessor: (rowOriginal) => rowOriginal.quality.value.toFixed(2),
        Cell: ({ row }) => {
            const quality = row.original.quality;

            return (
                <ColoredDotCell
                    color={getColorFromStatus(quality.fluxIndex, 'Quality')}
                    toolTipContent={String(quality.value)}
                />
            );
        },
    },
];

export const initialState: ITableColumnState = {
    sortBy: [{ id: 'projectNameSubProgram' }],
};

interface ScoreBoardValue {
    id: string;
    name?: string;
    value?: number;
    valueType: string;
    representation: string;
    color?: string;
}

interface ScoreBoardItem {
    name: string;
    value: ScoreBoardValue;
}

interface ItemAccumulator {
    key: string;
    value: ScoreBoardValue;
}

/**
 * serialize data from BE to be compatible with react-table rows structure
 * example: data = {
 *     "12": [
 *       {
 *         "name": "project",
 *         "value": {
 *           "id": "28",
 *           "name": "Tren Maya",
 *           "valueType": "text",
 *           "representation": "text"
 *         },
 *         "order": 1
 *       },
 *       {
 *         "name": "contract",
 *         "value": {
 *           "id": "117",
 *           "name": "test_2",
 *           "valueType": "text",
 *           "representation": "text"
 *         },
 *         "order": 2
 *       },
 *       {
 *         "name": "progress",
 *         "value": {
 *           "value": 0.82,
 *           "valueType": "number",
 *           "color": "Red",
 *           "representation": "color"
 *         },
 *         "order": 3
 *       },
 *       {
 *         "name": "risk",
 *         "value": {
 *           "value": 1.01,
 *           "valueType": "number",
 *           "color": "Amber",
 *           "representation": "color"
 *         },
 *         "order": 4
 *       },
 *       {
 *         "name": "quality",
 *         "value": {
 *           "value": 78.22,
 *           "valueType": "percent",
 *           "color": "Green",
 *           "representation": "color"
 *         },
 *         "order": 5
 *       },
 *       {
 *         "name": "duration",
 *         "value": {
 *           "value": 31,
 *           "valueType": "number",
 *           "representation": "text"
 *         },
 *         "order": 6
 *       },
 *       {
 *         "name": "finish_date",
 *         "value": {
 *           "value": 1.6666666666666667,
 *           "valueType": "number",
 *           "color": "Amber",
 *           "representation": "text"
 *         },
 *         "order": 7
 *       },
 *       {
 *         "name": "criticality_adherence",
 *         "value": {
 *           "value": [
 *             {
 *               "name": "result",
 *               "value": 0.82
 *             },
 *             {
 *               "name": "planned",
 *               "value": 42.86
 *             },
 *             {
 *               "name": "actual",
 *               "value": 35.16
 *             }
 *           ],
 *           "valueType": "number",
 *           "representation": "text"
 *         },
 *         "order": 8
 *       },
 *       {
 *         "name": "submitted_adherence",
 *         "value": {
 *           "value": [
 *             {
 *               "name": "result",
 *               "value": 0.97
 *             },
 *             {
 *               "name": "baseline",
 *               "value": 58.33
 *             },
 *             {
 *               "name": "latest",
 *               "value": 56.6
 *             }
 *           ],
 *           "valueType": "number",
 *           "representation": "text"
 *         },
 *         "order": 9
 *       }
 *     ],
 * }
 *
 * return [{
 *     "project": {
 *         "id": "28",
 *         "name": "Tren Maya",
 *         "valueType": "text",
 *         "representation": "text"
 *     },
 *     "contract": {
 *         "id": "111",
 *         "name": "big files",
 *         "valueType": "text",
 *         "representation": "text"
 *     },
 *     "progress": {
 *         "value": 0.71,
 *         "valueType": "number",
 *         "color": "Red",
 *         "representation": "color"
 *     },
 *     "risk": {
 *         "value": 1.05,
 *         "valueType": "number",
 *         "color": "Amber",
 *         "representation": "color"
 *     },
 *     "quality": {
 *         "value": 62.36,
 *         "valueType": "percent",
 *         "color": "Amber",
 *         "representation": "color"
 *     },
 *     "duration": {
 *         "value": 31,
 *         "valueType": "number",
 *         "representation": "text"
 *     },
 *     "finish_date": {
 *         "value": 0,
 *         "valueType": "number",
 *         "color": "Green",
 *         "representation": "text"
 *     },
 *     "criticality_adherence": {
 *         "value": [
 *             {
 *                 "name": "result",
 *                 "value": 0.71
 *             },
 *             {
 *                 "name": "planned",
 *                 "value": 3.41
 *             },
 *             {
 *                 "name": "actual",
 *                 "value": 2.42
 *             }
 *         ],
 *         "valueType": "number",
 *         "representation": "text"
 *     },
 *     "submitted_adherence": {
 *         "value": [
 *             {
 *                 "name": "result",
 *                 "value": 0
 *             },
 *             {
 *                 "name": "baseline",
 *                 "value": 5.72
 *             },
 *             {
 *                 "name": "latest",
 *                 "value": 0
 *             }
 *         ],
 *         "valueType": "number",
 *         "representation": "text"
 *     }
 * }]
 * @param data
 */
export const serializeScoreCardsData = (data = {}): ItemAccumulator[] => {
    return Object.keys(data).reduce((accumulator: ItemAccumulator[], key) => {
        accumulator.push(
            data[key].reduce((itemAccumulator: ItemAccumulator, item: ScoreBoardItem) => {
                itemAccumulator[item.name] = item.value;
                return itemAccumulator;
            }, {}),
        );
        return accumulator;
    }, []);
};
